import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  ArrowRight, Loader2, Check, Crown, Star, QrCode, Clock,
  Smartphone, BarChart3, Bell, MessageSquare, ShieldCheck, TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const VITRINE_PLANS = [
  {
    id: 'basico',
    name: 'Básico',
    price: '29,90',
    features: ['Até 15 avaliações/mes', 'Dashboard Completo', 'Respostas via WhatsApp e E-mail', 'QR Code + Placas para Balcão', '1 WhatsApp para alertas']
  },
  {
    id: 'pro',
    name: 'Pro',
    price: '39,90',
    features: ['Até 30 avaliações/mes', 'Logo Própria na Placa e no Sistema', 'PDF A4 para Impressão', 'Respostas via WhatsApp e E-mail', '2 WhatsApp para alertas']
  },
  {
    id: 'enterprise',
    name: 'Empresarial',
    price: '59,90',
    highlight: false,
    features: ['Até 60 avaliações/mes', 'Logo Própria no QR Code', 'Suporte Prioritário', 'Relatórios PDF e Excel', '3 WhatsApp para alertas']
  },
  {
    id: 'elite',
    name: 'Elite',
    price: '99,90',
    highlight: true,
    badge: 'Mais Vendido',
    features: ['Até 100 avaliações/mes', 'Tudo do Empresarial', 'Alerta de Crise (Nota Baixa)', 'Notificação por WhatsApp e Email', '4 WhatsApp para alertas']
  },
  {
    id: 'five_stars',
    name: '5 Estrelas',
    price: '149,90',
    highlight: false,
    features: ['Avaliações ILIMITADAS', 'Tudo do Elite', 'Automação Google Maps', 'Convite automático 5 estrelas no Google', 'WhatsApp ILIMITADOS']
  },
  {
    id: 'free',
    name: 'Free',
    price: '0',
    isFree: true,
    seal: 'Gratuito por 60 dias',
    features: ['Até 10 avaliações/mes', 'Dashboard Completo', 'Respostas via E-mail', 'QR Code + Placas para Balcão', '1 WhatsApp para alertas']
  }
];

/* ──── Mockup: Phone with QR Code ──── */
const PhoneMockup = () => (
  <div className="relative mx-auto w-[220px]">
    <div className="bg-slate-900 rounded-[2rem] p-2 shadow-2xl shadow-black/40 border border-slate-700">
      <div className="bg-[#0e3260] rounded-[1.5rem] overflow-hidden">
        <div className="h-6 flex items-center justify-center">
          <div className="w-16 h-1.5 bg-slate-700 rounded-full" />
        </div>
        <div className="px-4 pb-5 pt-2 text-center">
          <p className="text-[9px] text-white/40 mb-2 uppercase tracking-widest">Avaliação Rápida</p>
          <div className="bg-white rounded-xl p-3 mb-3">
            <QrCode className="w-16 h-16 text-[#0A2647] mx-auto" />
          </div>
          <div className="flex justify-center gap-1 mb-2">
            {[1,2,3,4,5].map(i => (
              <Star key={i} className={`w-5 h-5 ${i <= 4 ? 'text-[#C5A059] fill-[#C5A059]' : 'text-slate-600'}`} />
            ))}
          </div>
          <p className="text-[10px] text-white/50">Toque para avaliar</p>
        </div>
      </div>
    </div>
  </div>
);

/* ──── Mockup: Dashboard BI ──── */
const DashboardMockup = () => {
  const bars = [
    { label: 'Demora no Atendimento', pct: 100, count: 12 },
    { label: 'Qualidade do Pedido', pct: 66, count: 8 },
    { label: 'Limpeza/Higiene', pct: 33, count: 4 },
    { label: 'Temperatura do Prato', pct: 25, count: 3 },
  ];
  return (
    <div className="bg-slate-900/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-5 shadow-2xl shadow-black/30 max-w-sm mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-[#C5A059]" />
        <p className="text-xs font-bold text-white">Top Reclamacoes</p>
      </div>
      <div className="space-y-3">
        {bars.map(b => (
          <div key={b.label}>
            <div className="flex justify-between mb-1">
              <span className="text-[10px] text-white/70">{b.label}</span>
              <span className="text-[10px] font-bold text-red-400">{b.count}x</span>
            </div>
            <div className="h-2 rounded-full bg-slate-800">
              <div className="h-full rounded-full bg-gradient-to-r from-red-500 to-orange-500 transition-all duration-700"
                style={{ width: `${b.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ──── Mockup: WhatsApp Alert ──── */
const WhatsAppMockup = () => (
  <div className="bg-[#1a2e1a] rounded-2xl border border-emerald-800/50 p-4 shadow-2xl shadow-black/30 max-w-xs mx-auto">
    <div className="flex items-center gap-2 mb-3">
      <MessageSquare className="w-4 h-4 text-emerald-400" />
      <p className="text-[10px] font-bold text-emerald-400">ALERTA WHATSAPP</p>
    </div>
    <div className="bg-emerald-900/40 rounded-xl p-3">
      <p className="text-[11px] text-emerald-100 font-bold leading-relaxed">
        *ALERTA DE CRISE - Padaria do Joao*
      </p>
      <p className="text-[10px] text-emerald-200/70 mt-1">
        Cliente: Maria Silva<br/>
        Nota: 1.7/5<br/>
        "Pao borrachudo e cafe morno"
      </p>
      <p className="text-[10px] text-yellow-300/90 mt-2 font-semibold">
        Problemas: Frescor/Qualidade, Temperatura
      </p>
    </div>
  </div>
);


const AffiliateLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchAffiliate = async () => {
      try {
        const res = await fetch(`${API}/affiliates/public/${slug}`);
        if (!res.ok) throw new Error('Not found');
        const data = await res.json();
        setAffiliate(data);
        const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
        document.cookie = `ref_affiliate=${data.referral_code}; expires=${expires}; path=/`;
        document.cookie = `ref_slug=${data.slug}; expires=${expires}; path=/`;
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchAffiliate();
  }, [slug]);

  const handleSelectPlan = (planId) => {
    if (planId === 'free') {
      navigate('/register');
      return;
    }
    navigate(`/checkout/${planId}`);
  };

  const scrollToPlans = () => {
    document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-6">
        <div className="text-center">
          <img src={LOGO_URL} alt="FaloComODono" className="h-14 mx-auto mb-6 object-contain" />
          <p className="text-white/60 text-lg">Consultor não encontrado.</p>
          <Button onClick={() => navigate('/')} className="mt-6 bg-[#C5A059] text-[#0A2647] font-bold">Ir para o Site</Button>
        </div>
      </div>
    );
  }

  const firstName = affiliate?.name?.split(' ')[0] || 'Consultor';

  return (
    <div className="min-h-screen bg-[#0A2647] overflow-hidden" data-testid="affiliate-landing-page">

      {/* ═══════════ 1. ATENCAO — Hero ═══════════ */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A2647] via-[#0c2d52] to-[#0A2647]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#C5A059]/5 rounded-full blur-[120px]" />

        <div className="relative max-w-5xl mx-auto px-5 pt-10 pb-12 text-center">
          <img src={LOGO_URL} alt="FaloComODono" className="h-14 mx-auto mb-4 object-contain" />

          <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 border border-[#C5A059]/25 rounded-full px-5 py-2 mb-8">
            <Crown className="w-4 h-4 text-[#C5A059]" />
            <span className="text-sm font-medium text-[#C5A059]">Consultor {firstName}</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-5">
            Pare de perder clientes<br className="hidden sm:block" />
            <span className="text-[#C5A059]">para o silêncio.</span>
          </h1>

          <p className="text-base sm:text-lg text-white/50 max-w-2xl mx-auto mb-10 leading-relaxed">
            Descubra por que eles não estao voltando antes que reclamem na internet.
            O radar em tempo real para o dono de restaurante e padaria.
          </p>

          <Button onClick={scrollToPlans}
            className="h-14 px-8 text-base font-bold bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] rounded-xl shadow-2xl shadow-[#C5A059]/20 transition-all hover:scale-105 active:scale-95"
            data-testid="hero-cta-btn">
            Ver Planos e Preços
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* ═══════════ 2. INTERESSE — QR Code + Rapidez ═══════════ */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-[#071c38]" />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#C5A059]/10 rounded-full px-4 py-1.5 mb-5">
                <Clock className="w-3.5 h-3.5 text-[#C5A059]" />
                <span className="text-xs text-[#C5A059] font-semibold">10 SEGUNDOS</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-4">
                Avaliação em <span className="text-[#C5A059]">10 segundos.</span>
              </h2>
              <p className="text-white/50 leading-relaxed mb-6">
                Sem aplicativos, sem burocracia. O seu cliente fala com você enquanto
                ainda esta na mesa. Basta apontar a camera para o QR Code.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-[#C5A059]" />
                  <span className="text-sm text-white/60">QR Code exclusivo</span>
                </div>
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-[#C5A059]" />
                  <span className="text-sm text-white/60">Funciona em qualquer celular</span>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 3. DESEJO — BI + WhatsApp ═══════════ */}
      <section className="relative py-16">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A2647] via-[#0e3260] to-[#0A2647]" />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight mb-4">
              Tenha um <span className="text-[#C5A059]">raio-X</span> da sua operação.
            </h2>
            <p className="text-white/50 max-w-2xl mx-auto leading-relaxed">
              Saiba se o problema e o pao murcho, o banheiro sujo ou a demora do garcom.
              Recupere o cliente insatisfeito na hora e transforme uma nota baixa em um cliente fidelizado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            {/* BI Dashboard */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-[#C5A059]" />
                <p className="text-sm font-bold text-white">Dashboard Inteligente</p>
              </div>
              <DashboardMockup />
              <p className="text-xs text-white/30 text-center mt-3">
                Ranking em tempo real dos problemas mais relatados
              </p>
            </div>

            {/* WhatsApp Alert */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Bell className="w-5 h-5 text-[#C5A059]" />
                <p className="text-sm font-bold text-white">Alerta Instantâneo</p>
              </div>
              <WhatsAppMockup />
              <p className="text-xs text-white/30 text-center mt-3">
                Você recebe no WhatsApp e Email na hora da avaliação
              </p>
            </div>
          </div>

          {/* Trust stats */}
          <div className="grid grid-cols-3 gap-6 mt-14 max-w-lg mx-auto">
            {[
              { value: '10s', label: 'Para avaliar' },
              { value: '3s', label: 'Para alertar' },
              { value: '24/7', label: 'Monitoramento' }
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className="text-2xl font-extrabold text-[#C5A059]">{s.value}</p>
                <p className="text-[11px] text-white/40 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ 4. ACAO — Planos ═══════════ */}
      <section id="plans-section" className="relative py-16">
        <div className="absolute inset-0 bg-[#071c38]" />
        <div className="relative max-w-5xl mx-auto px-5">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
              Escolha o plano ideal
            </h2>
            <p className="text-white/40 text-sm">Cancele quando quiser. Sem fidelidade.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VITRINE_PLANS.map(plan => (
              <Card key={plan.id}
                className={`relative border-0 overflow-hidden transition-all hover:scale-[1.02] ${
                  plan.highlight
                    ? 'bg-gradient-to-b from-[#C5A059]/20 to-[#C5A059]/5 ring-2 ring-[#C5A059]/50 shadow-xl shadow-[#C5A059]/10'
                    : plan.isFree
                    ? 'bg-emerald-500/5 ring-1 ring-emerald-500/30'
                    : 'bg-white/5 backdrop-blur-sm'
                }`}
                data-testid={`plan-card-${plan.id}`}>
                {plan.badge && (
                  <div className="absolute top-0 right-0 bg-[#C5A059] text-[#0A2647] text-[10px] font-bold uppercase px-3 py-1 rounded-bl-lg">
                    {plan.badge}
                  </div>
                )}
                {plan.isFree && (
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-bold uppercase px-3 py-1 rounded-bl-lg">
                    {plan.seal}
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-white font-bold text-lg mb-1">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mb-5">
                    {plan.isFree ? (
                      <span className="text-3xl font-extrabold text-emerald-400">Grátis</span>
                    ) : (
                      <>
                        <span className="text-xs text-white/40">R$</span>
                        <span className="text-3xl font-extrabold text-[#C5A059]">{plan.price}</span>
                        <span className="text-xs text-white/40">/mes</span>
                      </>
                    )}
                  </div>
                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                        <Check className={`w-4 h-4 shrink-0 mt-0.5 ${plan.isFree ? 'text-emerald-400' : 'text-[#C5A059]'}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    className={`w-full h-12 font-bold text-sm rounded-xl transition-all active:scale-95 ${
                      plan.highlight
                        ? 'bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] shadow-lg'
                        : plan.isFree
                        ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg'
                        : 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                    }`}
                    data-testid={`plan-select-${plan.id}`}>
                    {plan.isFree ? 'Começar Grátis' : `Escolher ${plan.name}`}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ GARANTIA + FOOTER ═══════════ */}
      <section className="py-10">
        <div className="max-w-5xl mx-auto px-5 text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <span className="text-sm font-semibold text-emerald-400">Garantia de Satisfação</span>
          </div>
          <p className="text-white/40 text-sm max-w-lg mx-auto">
            Teste por 7 dias. Se não gostar, cancelamos sem custo.
            Seus dados ficam seguros e protegidos.
          </p>
        </div>
      </section>

      <div className="text-center py-6 border-t border-white/5">
        <p className="text-xs text-white/20">FaloComODono - Plataforma de Avaliações para Empresas</p>
      </div>
    </div>
  );
};

export default AffiliateLanding;
